import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-page min-h-screen flex items-center justify-center p-6">
      <div
        className="w-full max-w-md bg-white border border-[#CFD6DC] rounded-2xl p-8 shadow-xl"
        data-testid="admin-login-card"
      >
        <div className="flex items-center gap-3">
          <img src="/tbp-logo.png" alt="The Bookshelf Project" className="h-12 w-auto" />
        </div>
        <h1 className="mt-5 font-display text-3xl font-extrabold text-[#0E232E] leading-tight">
          Admin sign in
        </h1>
        <p className="mt-2 text-sm text-[#2F2E2E]">
          Manage pending submissions and curate the shelves.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
              Email
            </label>
            <input
              required
              type="email"
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">
              Password
            </label>
            <input
              required
              type="password"
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
            />
          </div>
          {error && (
            <div
              className="text-sm text-[#B23B28] bg-[#FDEEEA] border border-[#F5BDAE] rounded-md p-3"
              role="alert"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-2.5 rounded-md flex items-center justify-center gap-2"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <a
          href="/"
          className="block mt-6 text-center text-xs text-[#2B328C] hover:text-[#0E232E] underline font-semibold"
        >
          ← Back to The Bookshelf
        </a>
      </div>
    </main>
  );
}
